import { Box, Flex, RadioCards, Text } from '@scale/scaleui-radix';
import { RecurringType } from '@scale/shared/webinarSessions/types';
import { ErrorMessage } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/ErrorMessage';
import { Scheduler } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/Scheduler';
import { SessionTime, RepeatType } from 'react-scale/components/WebinarSessions/types';
import {
  WebinarError,
  WebinarErrorMessages,
} from 'react-scale/components/WebinarSessions/constants';
import { isNil } from 'lodash';

interface WebinarSessionsFormProps {
  sessionType: RecurringType;
  setSessionType: (sessionType: RecurringType) => void;
  setDuration: (duration: number | null) => void;
  eventStart: Date | null;
  setEventStart: (eventStart: Date | null) => void;
  numOccurrences: number | null;
  setNumOccurrences: (numOccurrences: number | null) => void;
  selectedDate: Date;
  setSelectedDate: (selectedDate: Date) => void;
  startTime: string;
  setStartTime: (startTime: string) => void;
  endTime: string;
  setEndTime: (endTime: string) => void;
  hostedBy: string[];
  setHostedBy: (hostedBy: string[]) => void;
  repeatNumber: number | null;
  setRepeatNumber: (repeatNumber: number | null) => void;
  repeatType: RepeatType;
  setRepeatType: (repeatType: RepeatType) => void;
  daysOfTheWeek: string[];
  setDaysOfTheWeek: (daysOfTheWeek: string[]) => void;
  additionalSessions: SessionTime[];
  setAdditionalSessions: (additionalSessions: SessionTime[]) => void;
  occurencesV2: SessionTime[];
  setOccurencesV2: (occurencesV2: SessionTime[]) => void;
  additionalTimesOfDayV2: SessionTime[];
  setAdditionalTimesOfDayV2: (additionalTimesOfDayV2: SessionTime[]) => void;
  getErrors: () => WebinarError[];
  getRecurringErrors: () => Record<number, WebinarError>;
  getAdditionalSessionsErrors: () => Record<number, WebinarError>;
}

const SESSION_TYPES = [
  {
    id: RecurringType.MULTI, // For new webinar creation, we default to multi-session
    title: 'One or multiple sessions',
    description: 'An individual session or sessions at different times',
  },
  {
    id: RecurringType.RECURRING,
    title: 'Recurring',
    description: 'Sessions that repeat regularly',
  },
];

const WebinarSessionsForm = ({
  sessionType,
  setSessionType,
  setDuration,
  eventStart,
  setEventStart,
  numOccurrences,
  setNumOccurrences,
  selectedDate,
  setSelectedDate,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  hostedBy,
  setHostedBy,
  repeatNumber,
  setRepeatNumber,
  repeatType,
  setRepeatType,
  daysOfTheWeek,
  setDaysOfTheWeek,
  additionalSessions,
  setAdditionalSessions,
  occurencesV2,
  setOccurencesV2,
  additionalTimesOfDayV2,
  setAdditionalTimesOfDayV2,
  getErrors,
  getRecurringErrors,
  getAdditionalSessionsErrors,
}: WebinarSessionsFormProps) => {
  const webinarErrors = getErrors();

  return (
    <Flex direction="column" gap="4">
      <Flex direction="column" gap="1">
        <Flex direction="row" justify="between">
          <Text as="label" size="2" weight="medium">
            Session type
          </Text>
          <Text size="1" color="neutral" weight="regular">
            Local Timezone - {Intl.DateTimeFormat().resolvedOptions().timeZone}
          </Text>
        </Flex>
        <RadioCards.Root
          size="1"
          value={sessionType ?? undefined}
          onValueChange={(value: string) => setSessionType(value as RecurringType)}
          className="w-full"
        >
          <Flex direction="row" gap="2" width="100%">
            {SESSION_TYPES.map(sessionType => (
              <RadioCards.Item
                key={sessionType.title}
                value={sessionType.id}
                className="justify-start w-full"
              >
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    {sessionType.title}
                  </Text>
                  <Text size="1" color="gray">
                    {sessionType.description}
                  </Text>
                </Flex>
              </RadioCards.Item>
            ))}
          </Flex>
        </RadioCards.Root>
        {webinarErrors.includes(WebinarError.START_TIME_REQUIRED) && (
          <ErrorMessage text={WebinarErrorMessages[WebinarError.START_TIME_REQUIRED]} />
        )}
      </Flex>

      {!isNil(sessionType) && (
        <Box>
          <Scheduler
            sessionType={sessionType}
            setDuration={setDuration}
            eventStart={eventStart}
            setEventStart={setEventStart}
            numOccurrences={numOccurrences}
            setNumOccurrences={setNumOccurrences}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            startTime={startTime}
            setStartTime={setStartTime}
            endTime={endTime}
            hostedBy={hostedBy}
            setHostedBy={setHostedBy}
            setEndTime={setEndTime}
            repeatNumber={repeatNumber}
            setRepeatNumber={setRepeatNumber}
            repeatType={repeatType}
            setRepeatType={setRepeatType}
            daysOfTheWeek={daysOfTheWeek}
            setDaysOfTheWeek={setDaysOfTheWeek}
            additionalSessions={additionalSessions}
            setAdditionalSessions={setAdditionalSessions}
            occurencesV2={occurencesV2}
            setOccurencesV2={setOccurencesV2}
            additionalTimesOfDayV2={additionalTimesOfDayV2}
            setAdditionalTimesOfDayV2={setAdditionalTimesOfDayV2}
            getErrors={getErrors}
            getRecurringErrors={getRecurringErrors}
            getAdditionalSessionsErrors={getAdditionalSessionsErrors}
          />
        </Box>
      )}
    </Flex>
  );
};

export default WebinarSessionsForm;
